/**
 * Account Features utilities
 */

const ocrUnlimitedPageLimit = 0;
const ocrHardPageLimit = 200;
const ocrDefaultPageLimit = 20;
const ocrNotAllowed = -1;

function getPrettySubscriptionTypeName(subscriptionType: string): string {
	switch (subscriptionType) {
		case "free":
			return "Free";
		case "promotional":
			return "Promotional";
		case "premium_monthly":
			return "Monthly Premium";
		case "premium_yearly":
			return "Annual Premium";
		case "institutional_premium":
			return "Institutional Premium";
		default:
			return "Unknown";
	}
}

/**
 * Determine the type of support, and how much trial time is remaining (if
 * appropriate).
 *
 * Possible types of support are:
 * none       - account email is unverified after the grace period
 * unverified - account is recently created, email unverified
 * read       - account is able to view things but not edit
 * write      - account is able to view and edit things
 * subNeeded  - account is not able to view or do anything except subscribe
 *
 * @param {Object} account
 * @return {Object}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function getSupportLevelData(account) {
	let supportLevel = "none";
	let hoursLeft = 0;

	const currentDate = new Date();
	const features = (account?.features) ? account.features : null;

	if (features !== null) {
		// Helper functions
		// @ts-expect-error FIXME
		const findFeature = (featureName) => {
			// @ts-expect-error FIXME
			return features.find(obj => obj.feature_name === featureName) || null;
		};

		// @ts-expect-error FIXME
		const calculateSupportLevelData = function (level, feature) {
			const expireDate = (feature.expire_dt == null)
				? null
				: new Date(feature.expire_dt);

			if (!expireDate)
				hoursLeft = 0;
			else
			// @ts-expect-error FIXME
				hoursLeft = Math.round((expireDate - currentDate) / 3600000);

			return {
				supportLevel: level,
				hoursLeft
			};
		};

		// @ts-expect-error FIXME
		const isActive = (feature) => {
			if (feature.expire_dt == null)
				return true;
			else {
				const expireDate = new Date(feature.expire_dt);
				return expireDate > currentDate;
			}
		};

		// Feature objects, or null
		const fWrite = findFeature("write");
		const fBasic = findFeature("basic");
		const fRead = findFeature("read");
		const fTrial = findFeature("trial");

		// Helper flag
		let waitingForLowerLevel = false;

		// Write feature exists
		if (fWrite) {
			if (isActive(fWrite)) {
				// Write is active, we've reached a conclusion
				return calculateSupportLevelData("write", fWrite);
			} else {
				// Write expired, see if "basic" or "read" levels are active later
				waitingForLowerLevel = true;
			}
		}

		// Basic feature exists. User is either on a legacy subscription, or
		// they verified their email
		if (fBasic) {
			if (isActive(fBasic)) {
				// Basic is active, we've reached a conclusion
				return calculateSupportLevelData("write", fBasic);
			} else if (fRead && isActive(fRead)) {
				// User can still read things with their account, but not edit
				return calculateSupportLevelData("read", fRead);
			} else
				supportLevel = "subNeeded";
		}

		// Subscription expired, and none of the other permission-granting
		// features are active. The only action the user can take is to subscribe.
		if (waitingForLowerLevel) {
			supportLevel = "subNeeded";
			return {supportLevel, hoursLeft};
		}

		// User hasn't verified their email yet, and that time limit isn't up
		// yet. We should show them things to ask them to verify their email.
		if (isActive(fTrial))
			return calculateSupportLevelData("unverified", fTrial);
	}

	return {supportLevel, hoursLeft};
}

/**
 * If we haven't created as many projects as we're allowed,
 * we still have more available projects.
 *
 * @param {Object} account
 * @param {Object[]} projects
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function hasMoreAvailableProjects(account, projects) {
	if (!account?.available_projects)
		return true; // unlimited or missing data

	// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
	const ownActiveProjects = projects.filter(project => {
		const isActive = (project.status === "active" || !("status" in project));
		const isMine = (project.account_uuid === account.uuid);

		return isActive && isMine;
	});

	return account.available_projects > ownActiveProjects.length;
}

/**
 * Checks to see if this is a developer account
 *
 * @param {Object} account
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isDeveloper(account) {
	const accountRoles = account.roles || [];
	return accountRoles.includes("developer");
}

/**
 * Checks to see if this is an admin account
 *
 * @param {Object} account
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isAdmin(account) {
	const accountRoles = account.roles || [];
	return accountRoles.includes("admin") || accountRoles.includes("superadmin");
}

/**
 * Checks to see if this is a super-admin account
 *
 * @param {Object} account
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isSuperAdmin(account) {
	const accountRoles = account.roles || [];
	return accountRoles.includes("superadmin");
}

/**
 * Checks to see if this is a reporter account (but not an admin)
 *
 * @param {Object} account
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isReporterOnly(account) {
	if (isAdmin(account))
		return false;
	const accountRoles = account.roles || [];
	return accountRoles.includes("reporter") || accountRoles.includes("org-reporter");
}

/**
 * Checks to see if this is an org reporter account (not just a general reporter), and if they belong to an org.
 *
 * @param {Object} account
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isOrgReporterOnly(account) {
	if (isAdmin(account))
		return false;
	const accountRoles = account.roles || [];
	if (!accountRoles.includes("org-reporter") || accountRoles.includes("reporter"))
		return false;
	return !!(account.organization?.uuid);
}

/**
 * Checks to see if this is an org admin account.
 *
 * @param {Object} account
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isOrgReporter(account) {
	const accountRoles = account.roles || [];
	return !!(accountRoles.includes("org-reporter") && account.organization?.uuid);
}

/**
 * Checks to see if this is an org admin account.
 *
 * @param {Object} account
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isOrgAdmin(account) {
	const accountRoles = account.roles || [];
	return !!(accountRoles.includes("org-admin") && account.organization?.uuid);
}

/**
 * Checks to see if this account has admin, finance or teacher access to
 * communities. If a communityUuid is included, only that community will
 * be checked.
 *
 * @param {Object} account
 * @param {string} [communityUuid]
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function hasCommunityNonStudentAccess(account, communityUuid = null) {
	const accountRoles = account.roles || [];
	if (accountRoles.length > 0)
		return false;

	const communities = ((account.communities || []) as {community_uuid: string; roles: string[]}[])
		// Remove deleted communities
		.filter(c => account?.computed?.communities && (account?.computed?.communities[c.community_uuid] ?? false));

	if (communityUuid) {
		const community = communities.find(c => c.community_uuid === communityUuid);
		const roles = community?.roles ?? [];
		return roles.includes("admin") || roles.includes("finance") || roles.includes("teacher");
	} else {
		const communityRoles = communities?.flatMap(community => community.roles);
		return communityRoles.includes("admin") || communityRoles.includes("finance") || communityRoles.includes("teacher");
	}
}

/**
 * Checks to see if this account has admin access to communities. If a
 * communityUuid is included, only that community will be checked.
 *
 * @param {Object} account
 * @param {string} [communityUuid]
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function hasCommunityAdminAccess(account, communityUuid = null) {
	const accountRoles = account.roles || [];
	if (accountRoles.length > 0)
		return false;

	const communities = ((account.communities || []) as {community_uuid: string; roles: string[]}[])
		// Remove deleted communities
		.filter(c => account?.computed?.communities && (account?.computed?.communities[c.community_uuid] ?? false));

	if (communityUuid) {
		const community = communities.find(c => c.community_uuid === communityUuid);
		const roles = community?.roles ?? [];
		return roles.includes("admin");
	} else {
		const communityRoles = communities.flatMap(community => community.roles);
		return communityRoles.includes("admin");
	}
}

/**
 *
 * @param {Object} account
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isSubscriptionExpired(account) {
	if (!account || !("subscription" in account))
		return false;

	const subscription = account.subscription;

	if (!subscription)
		return false; // assume free subscription

	const expire_dt = subscription.expire_dt || null;
	if (expire_dt === null || (expire_dt && new Date(expire_dt) > new Date()))
		return false; // expires in the future

	return true;
}

/**
 * Used by refreshAccountData() to determine when to refresh next.
 * Returns:
 *   - null when no expiration
 *   - positive integer when expiration in future
 *   - negative integer when expiration has passed
 *
 * @param {Object} account
 * @return {number|null}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function getSecondsToSubscriptionExpiration(account) {
	const expire_dt = account.subscription.expire_dt;
	if (!expire_dt)
		return null;

	// @ts-expect-error TS(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
	const msDiff = new Date(expire_dt) - new Date();
	const sDiff = Math.ceil(msDiff / 1000);

	return sDiff;
}

/**
 * Gets feature data by a given name from the account.
 *
 * @param {Object} account
 * @param {string} name
 * @return {Object|undefined}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function getFeatureByName(account, name) {
	if (!account || account.features === undefined)
		return undefined;

	// @ts-expect-error TS(7006) FIXME: Parameter 'f' implicitly has an 'any' type.
	return (account.features).find(f => f.feature_name === name);
}

/**
 * Checks if the account is allowed to convert the PDF
 *
 * @param {Object} account
 * @param {number} pageCount
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isAllowedToConvertPdf(account, pageCount) {
	const maxPages = getPdfOcrMaxPages(account);

	switch (maxPages) {
		case ocrNotAllowed:
			return false;
		case ocrUnlimitedPageLimit:
			return pageCount <= ocrHardPageLimit;
		default:
			return pageCount <= maxPages;
	}
}

/**
 * Returns the maximum number of pages the user is allowed
 * to OCR in a single PDF. Special cases:
 *	0:  unlimited
 *	-1: none
 *
 * @param {Object} account
 * @return {number}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function getPdfOcrMaxPages(account) {
	if (isSubscriptionExpired(account))
		return ocrNotAllowed;

	const ocrFeature = getFeatureByName(account, "ocr") || {};

	let maxPages;
	if (ocrFeature.n_pages)
		maxPages = ocrFeature.n_pages;
	else if (account.subscription.type === "free")
		maxPages = ocrDefaultPageLimit; // user has free account but missing ocr feature data
	else
		maxPages = ocrUnlimitedPageLimit; // paid accounts

	return maxPages;
}

/**
 * Enables UI elements related to bookmarks (e.g. "Bookmark" context menu item, (un)bookmark button on sidebar header)
 * @return {boolean}
 */
function isBookmarksEnabled() {
	return true;
}

/**
 * Enables UI elements related to sharing (Sharing buttons, "Shared with me" and
 * "Shared by me" projects, avatar lists)
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isSharingEnabled(account) {
	// Enable sharing for all on dev environment
	if (location.host === "localhost:10000")
		return true;

	// Enable sharing for everyone
	return true;
}

// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isCommentingEnabled(account) {
	if (!isSharingEnabled(account))
		return false;

	// Enable commenting for all on dev environment
	if (location.host === "localhost:10000")
		return true;

	// TODO: Probably add a condition later some time
	return true;
}

function isImageCaptureEnabled() {
	return true;
}

// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
function isNotificationsEnabled(account) {
	return true;
}

export default {
	getFeatureByName,
	getPdfOcrMaxPages,
	getPrettySubscriptionTypeName,
	getSecondsToSubscriptionExpiration,
	getSupportLevelData,
	hasMoreAvailableProjects,
	isDeveloper,
	isAdmin,
	isSuperAdmin,
	isReporterOnly,
	isOrgReporterOnly,
	isOrgReporter,
	isOrgAdmin,
	hasCommunityNonStudentAccess,
	hasCommunityAdminAccess,
	isAllowedToConvertPdf,
	isBookmarksEnabled,
	isCommentingEnabled,
	isImageCaptureEnabled,
	isNotificationsEnabled,
	isSharingEnabled,
	isSubscriptionExpired,
	ocrHardPageLimit,
	ocrNotAllowed,
	ocrUnlimitedPageLimit
};
